import { useState } from "react";
import { Check, Columns3, Crown, ListChecks, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { planCost, planId } from "@/lib/plans";
import { cn } from "@/lib/utils";
import { PlanCompare } from "@/components/login/PlanCompare";
import { RECRUITER_PERK } from "@/lib/recruiter";

export const planGroups = (p) => {
  const g = (p.groups || []).filter((x) => x.items?.length);
  const base = g.length ? g : [{ title: "What's included", items: p.features || p.perks || [] }];
  const perk = RECRUITER_PERK[planId(p)];
  return !perk || base.some((x) => x.items.includes(perk)) ? base : [...base, { title: "Human recruiter support", items: [perk] }];
};
export const featureCount = (p) => planGroups(p).reduce((n, g) => n + g.items.length, 0);

const Header = ({ p, yearly }) => {
  const id = planId(p);
  const cost = planCost(p, yearly);
  return (
    <div className="relative overflow-hidden border-b border-[#D4AF37]/25 bg-gradient-to-br from-[#3a2e0c] via-[#1d170a] to-[#121214] px-6 pb-6 pt-7">
      <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#D4AF37]/25 blur-3xl" />
      <div className="relative flex flex-wrap items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#F3C649] text-black shadow-[0_0_24px_rgba(212,175,55,0.45)]">{id === "elite" ? <Crown className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}</span>
        <DialogTitle className="font-display text-2xl text-zinc-50" data-testid="plan-details-name">{p.name || id}</DialogTitle>
        {p.badge && <span className="rounded-full bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">{p.badge}</span>}
      </div>
      <div className="relative mt-4 flex items-baseline gap-1"><span className="font-display text-4xl font-semibold text-zinc-50" data-testid="plan-details-price">{cost.price}</span><span className="text-zinc-400">/{cost.unit}</span></div>
      {cost.note && <p className="relative mt-1 text-xs font-medium text-emerald-400">{cost.note}</p>}
      {(p.tagline || p.description) && <p className="relative mt-2 max-w-lg text-sm text-zinc-300">{p.tagline || p.description}</p>}
    </div>
  );
};

export const PlanDetails = ({ plans, active, yearly, onPick, onClose, onCta, footer }) => {
  const [view, setView] = useState("details");
  const p = plans.find((x) => planId(x) === active);
  if (!p) return null;
  const isFree = planId(p) === "free";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={cn("max-h-[90vh] gap-0 overflow-y-auto border-[#D4AF37]/40 bg-[#121214] p-0 sm:p-0", view === "compare" ? "sm:max-w-3xl" : "sm:max-w-2xl")} data-testid="plan-details-dialog">
        <Header p={p} yearly={yearly} />
        <div className="flex flex-wrap items-center gap-2 border-b border-[#27272A] px-4 py-2">
          <div className="inline-flex rounded-full border border-[#D4AF37]/30 bg-[#09090B] p-0.5" data-testid="plan-view-toggle">
            {[["details", "Plan details", ListChecks], ["compare", "Compare plans", Columns3]].map(([v, l, Icon]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`plan-view-${v}`}
                className={cn("flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors", view === v ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:text-zinc-100")}>
                <Icon className="h-3.5 w-3.5" />{l}
              </button>
            ))}
          </div>
          {view === "details" && <div className="flex gap-1 overflow-x-auto" data-testid="plan-details-tabs">
          {plans.map((x) => (
            <button key={planId(x)} onClick={() => onPick(planId(x))} data-testid={`plan-details-tab-${planId(x)}`}
              className={cn("shrink-0 rounded-full px-3 py-1 text-xs transition-colors", planId(x) === active ? "bg-[#33290D] font-semibold text-[#F3C649]" : "text-zinc-400 hover:text-zinc-100")}>
              {x.name || planId(x)} · {featureCount(x)}
            </button>
          ))}
          </div>}
        </div>
        {view === "compare" ? <PlanCompare plans={plans} active={active} yearly={yearly} onPick={onPick} /> : (
        <div className="grid gap-x-6 gap-y-5 p-6 sm:grid-cols-2" data-testid="plan-details-features">
          {planGroups(p).map((g, gi) => (
            <div key={g.title} className="lp-rise" style={{ animationDelay: `${gi * 70}ms` }}>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">{g.title}</div>
              <ul className="space-y-2">
                {g.items.map((t) => <li key={t} className="flex gap-2 text-sm leading-snug text-zinc-200"><span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#33290D] text-[#F3C649]"><Check className="h-3 w-3" strokeWidth={3} /></span>{t}</li>)}
              </ul>
            </div>
          ))}
        </div>
        )}
        <div className="sticky bottom-0 border-t border-[#27272A] bg-[#121214]/95 p-4 backdrop-blur-md">
          {footer ? footer(p) : <>
          <button onClick={onCta} data-testid="plan-details-cta"
            className="lp-shine relative w-full overflow-hidden rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] px-6 py-3 text-sm font-semibold text-black shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-shadow hover:shadow-[0_0_40px_rgba(243,198,73,0.5)]">
            {isFree ? "Start free" : "Start my free 7-day trial"}
          </button>
          <p className="mt-2 text-center text-[11px] text-zinc-500">Every new account starts with a free 7-day Elite trial. Cancel any time.</p>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
